import axios, { AxiosRequestConfig } from 'axios';
// config
import { HOST_API } from 'src/config-global';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({
  baseURL: HOST_API,
  maxRedirects: 0 // Prevent following redirects
});

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => {
    // Log the error for debugging
    console.error('Axios error:', error);

    // If the error is a redirect (status 302), log it
    if (error.response && error.response.status === 302) {
      console.error('Redirect detected:', error.response.headers.location);
    }

    return Promise.reject((error.response && error.response.data) || 'Something went wrong');
  }
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcherList = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};
export const fetcherPaginatedList = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data.content;
};

export const fetcherPaginatedListWithMeta = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return {
    content: res.data.content,
    totalElements: res.data.totalElements,
    totalPages: res.data.totalPages,
    size: res.data.size,
    number: res.data.number,
  };
};
export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};

// ----------------------------------------------------------------------

export const endpoints = {
  chat: '/api/chat',
  kanban: '/api/kanban',
  calendar: '/api/calendar',
  auth: {
    me: '/api/auth/me',
    login: '/api/auth/login',
    register: '/api/auth/register',
    googleLogin: '/api/auth/oauth2/google',
    facebookLogin: '/api/auth/oauth2/facebook',
  },
  users: {
    list: '/api/users',
    details: (id: string | number) => `/api/users/${id}`,
    updateRole: (id: string | number) => `/api/users/${id}/role`,
    resetPassword: (id: string | number) => `/api/users/${id}/reset-password`,
  },
  mail: {
    list: '/api/mail/list',
    details: '/api/mail/details',
    labels: '/api/mail/labels',
  },
  post: {
    list: '/api/post/list',
    details: '/api/post/details',
    latest: '/api/post/latest',
    search: '/api/post/search',
  },
  product: {
    list: '/api/product/list',
    details: '/api/product/details',
    search: '/api/product/search',
  },
  poets: {
    list: '/api/poets',
    details: (id: string | number) => `/api/poets/${id}`,
    poems: (id: string | number) => `/api/poets/${id}/poems`,
  },
  poems: {
    list: '/api/poems',
    details: (id: string | number) => `/api/poems/${id}`,
    search: '/api/poems/search',
    filter: '/api/poems/filter',
    view: (id: string | number) => `/api/poems/${id}/view`,
    statistics: (word: string) => `/api/poems/statistics/word?word=${encodeURIComponent(word)}`,
  },
  tags: {
    list: '/api/tags',
    details: (id: string | number) => `/api/tags/${id}`,
    poems: (id: string | number) => `/api/tags/${id}/poems`,
  },
  audios: {
    list: '/api/audios',
    details: (id: string | number) => `/api/audios/${id}`,
    byPoem: (poemId: string | number) => `/api/audios/poem/${poemId}`,
    download: (fileName: string) => `/api/audios/download/${fileName}`,
    stream: (fileName: string) => `/api/audios/stream/${fileName}`,
    track: (fileName: string) => `/api/audios/track/${fileName}`,
  },
  singers: {
    list: '/api/singers',
    details: (id: string | number) => `/api/singers/${id}`,
    audios: (id: string | number) => `/api/singers/${id}/audios`,
  },
  difficultWords: {
    list: '/api/difficult-words',
    details: (id: string | number) => `/api/difficult-words/${id}`,
    byPoem: (poemId: string | number) => `/api/difficult-words/poem/${poemId}`,
    search: (word: string) => `/api/difficult-words/search?word=${encodeURIComponent(word)}`,
  },
};
